import type { ConfiguratorPageDefinition, StepHardcodedData } from "~/features/configurator/ConfiguratorPageDefinition";
import * as RailCstic from "~/features/configurator/Rail/v2/configuration/tklt/TKLTRailsCstics";
import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import { TKLT_RAIL_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { BASE_DESIGNATION_SEPARATOR } from "~/features/configurator/Linear/constants";
import { type LinearConfiguration, type LinearCstic, type LinearSubstep } from "~/features/configurator/Linear/models";
import { getHighestVisitedStep } from "~/features/configurator/Linear/redux/selectors";
import { sendRailAddToCart } from "~/features/configurator/Rail/api/sendRailAddToCart";
import { sendRailUpdate } from "~/features/configurator/Rail/api/sendRailConfiguratorUpdate";
import { sendRailResetConfigurator } from "~/features/configurator/Rail/api/sendRailResetConfigurator";
import { sendRailMiscChange, sendRailStepChange } from "~/features/configurator/Rail/api/sendRailStepChange";
import { sendRailDataReset } from "~/features/configurator/Rail/api/sendRailStepReset";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import type { RootState } from "~/app/store";
import { csticHasValue, getCsticsValue, join } from "~/features/configurator/common/v2/helper";
import { TKLTRailSummaryData } from "~/features/configurator/Rail/v2/configuration/tklt/summary/TKLTRailSummaryData";
import { makeSendStepCompletedEvent, makeSummaryDataProvider } from "~/features/configurator/Shaft/shaftGaHelper";
import { transformSummaryDataToV2, transformToSelectionData } from "~/features/configurator/Shaft/SummaryDataProvider";
import {
  getStep1SummaryData,
  getStep2SummaryData,
  getStep3SummaryData,
  getStep4SummaryData,
} from "~/features/configurator/Rail/v2/configuration/tklt/summary/TKLTSummaryDataProvider";
import { validateFromDefinition } from "~/features/configurator/common/v2/validation";

const RAIL_CONFIGURATOR_STEP_DATA: StepHardcodedData[] = [
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step1Data = getStep1SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(1),
        gaName: summaryDataProvider.getGaStepName(1),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step1Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = RAIL_CONFIGURATOR_STEP_DATA[0].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step2Data = getStep2SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(2),
        gaName: summaryDataProvider.getGaStepName(2),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step2Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = RAIL_CONFIGURATOR_STEP_DATA[1].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step3Data = getStep3SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(3),
        gaName: summaryDataProvider.getGaStepName(3),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step3Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = RAIL_CONFIGURATOR_STEP_DATA[2].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step4Data = getStep4SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(4),
        gaName: summaryDataProvider.getGaStepName(4),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step4Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = RAIL_CONFIGURATOR_STEP_DATA[3].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
];

export const TKLT_RAIL_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.Rail,
  productCode: TKLT_RAIL_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.rail.name",
  },
  apiCalls: {
    update: sendRailUpdate,
    reset: sendRailResetConfigurator,
    stepChange: sendRailStepChange,
    stepReset: sendRailDataReset,
    miscChange: sendRailMiscChange,
    addToCart: sendRailAddToCart,
  },
  summary: TKLTRailSummaryData,
  isAddToCartEnabled: (state: RootState) => {
    const kmats = state.linearConfigurator.kmats;
    const conflicts = state.linearConfigurator.conflicts;

    const validations = [0, 1, 2, 3].map(
      (step) =>
        state.linearConfigurator.steps[step].substeps?.every(
          (substep) =>
            validateFromDefinition(state.linearConfigurator.substeps[substep] as LinearSubstep, kmats, conflicts)
              .substepValid,
        ) ?? false,
    );

    const addToCartEnabled = validations.every((validation) => validation);

    return addToCartEnabled;
  },
  isSaveEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    const kmat = getMainKmat({ kmats: state.linearConfigurator.kmats });
    const hasLengthSet = csticHasValue(kmat, RailCstic.GuidewayLength);

    return step >= 1 && hasLengthSet;
  },
  stepHardcodedData: RAIL_CONFIGURATOR_STEP_DATA,
  isConflictAvoidable: (csticName) => {
    return [RailCstic.GuidewayLength, RailCstic.AL, RailCstic.AR].includes(csticName);
  },
  generateDesignation: (kmats) => {
    const kmat = getMainKmat({ kmats });

    const [series, sizeCode, version] = getCsticsValue(
      kmat,
      RailCstic.DesignType_Series,
      RailCstic.DesignType_SizeCode,
      RailCstic.DesignType_Version,
    );

    return join("-", join("", series, sizeCode), version) || BASE_DESIGNATION_SEPARATOR;
  },
  gaConfiguratorName: "kllt-rail",
  modelAndUpdatePreprocessor: (model) => {
    const kmat = getMainKmat({ kmats: model.kmats });
    const cstics = kmat.cstics;

    const lengthCstic = cstics[RailCstic.GuidewayLength];
    if (lengthCstic) {
      lengthCstic.label = "configurator.rail.cstic.guidewayLength.name";
    }

    const alCstic = cstics[RailCstic.AL];
    if (alCstic) {
      alCstic.label = "configurator.rail.section.al.name";
    }

    const arCstic = cstics[RailCstic.AR];
    if (arCstic) {
      arCstic.label = "configurator.rail.section.ar.name";
    }

    const partSizingCstic = cstics[RailCstic.PartSizing];
    if (partSizingCstic) {
      partSizingCstic.label = "configurator.rail.cstic.partSizing.name";
      partSizingCstic.values.forEach((val) => {
        val.label = `configurator.rail.cstic.partSizing.${val.value}.name`;
      });
    }

    const partAmountCstic = cstics[RailCstic.PartAmount];
    if (partAmountCstic) {
      partAmountCstic.label = "configurator.rail.cstic.partAmount.name";
    }

    return model;
  },
  postDefinitionProcessor: (model, dto) => {
    model.allowedSteps = calculateAllowedSteps(model);
    return model;
  },
  canQueryAvailability: (state) => {
    const kmats = state.linearConfigurator.kmats;
    const cstics = getMainKmat({ kmats }).cstics;

    const hasAllRequiredCstics = [
      RailCstic.GuidewayDesign,
      RailCstic.Size,
      RailCstic.Coating,
      RailCstic.Accuracy,
    ].every((cstic) => !!cstics[cstic].value);

    return hasAllRequiredCstics;
  },
} as const;

const EMPTY_FAKE_CSTIC: LinearCstic = {
  key: "",
  name: "",
  label: "",
  value: "",
  groupId: "",
  values: [],
  type: "",
  info: "",
  description: "",
  readonly: false,
  fake: true,
};

function calculateAllowedSteps(model: LinearConfiguration | LinearConfiguration): boolean[][] {
  const kmats = model.kmats;
  const conflicts = "conflictedCstics" in model ? model.conflictedCstics : [];

  const [step1Valid, step2Valid, step3Valid, step4Valid] = [0, 1, 2, 3].map(
    (step) =>
      model.steps[step].substeps?.every(
        (substep) => validateFromDefinition(model.substeps[substep] as LinearSubstep, kmats, conflicts).substepValid,
      ) ?? false,
  );

  const canVisitPage2 = step1Valid;
  const canVisitPage3 = step2Valid && canVisitPage2;
  const canVisitPage4 = step3Valid && canVisitPage3;
  const canVisitPage5 = step4Valid && canVisitPage4;

  const allowedSteps = [
    [true, canVisitPage2, canVisitPage3, canVisitPage4, canVisitPage5],
    [true, true, canVisitPage3, canVisitPage4, canVisitPage5],
    [true, true, true, canVisitPage4, canVisitPage5],
    [true, true, true, true, canVisitPage5],
    [true, true, true, true, true],
  ];

  return allowedSteps;
}

const sendStepCompletedEvent = makeSendStepCompletedEvent(TKLT_RAIL_DEFINITION);
